/**
 * DAT Contract Form Component
 * Creates DAT contracts with pre-filled data from case
 */

import { useState, useEffect } from 'react';
import { createOrUpdateContract, getContract, mapCaseToDossier, formatCurrency, CONTRACT_TYPES, generateCalculationPdf } from '../services/myClaimService';
import { checkDatCredentials } from '../services/datService';
import { getBackendUrl } from '../utils/apiUrl';
import { toast } from 'sonner';
import { Loader2, CheckCircle, AlertTriangle, FileText, Download, RefreshCw, Car, User, Calculator, Euro } from 'lucide-react';

function DatContractForm({ 
  type = 'glass', 
  caseData = null,
  caseId = null,
  caseNumber = null,
  onContractCreated,
  onCalculationComplete,
  onClose 
}) {
  const config = CONTRACT_TYPES[type] || CONTRACT_TYPES.glass;
  
  const [formData, setFormData] = useState({
    name: '',
    country: 'DE',
    language: 'de',
    currency: 'EUR',
    owner: {
      title: 'titleMr',
      firstName: '',
      lastName: '',
      street: '',
      streetNumber: '',
      zipCode: '',
      city: '',
      phone: '',
      email: '',
    },
    vehicle: {
      vin: '',
      licensePlate: '',
      mileage: '',
    },
  });

  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [claimId, setClaimId] = useState(null);
  const [error, setError] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [hasCredentials, setHasCredentials] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Check DAT credentials on mount
  useEffect(() => {
    const checkCredentials = async () => {
      const result = await checkDatCredentials();
      setHasCredentials(result.hasCredentials);
    };
    checkCredentials();
  }, []);

  // Pre-fill form with case data
  useEffect(() => {
    if (caseData) {
      const dossier = mapCaseToDossier(caseData, caseNumber || `${type.toUpperCase()}-${Date.now()}`);
      setFormData(dossier);
    }
  }, [caseData, caseNumber, type]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOwnerChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      owner: { ...prev.owner, [name]: value }
    }));
  };

  const handleVehicleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      vehicle: { ...prev.vehicle, [name]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError(null);

    try {
      const newClaimId = await createOrUpdateContract(
        formData,
        config.contractType,
        'DAT',
        config.invoiceRatesId
      );
      
      setClaimId(newClaimId);
      setStatus('success');
      toast.success(`${config.label} erstellt! Claim ID: ${newClaimId}`);

      if (onContractCreated) {
        onContractCreated(newClaimId, type);
      }
    } catch (err) {
      setError(err.message);
      setStatus('error');
      toast.error(`Fehler: ${err.message}`);
    }
  };

  const fetchContractData = async () => {
    if (!claimId) return;
    
    setLoadingDetails(true);
    try {
      const data = await getContract(claimId);
      setContractData(data);
      
      if (onCalculationComplete && data.calculation) {
        onCalculationComplete(data, claimId, type);
      }
      
      return data;
    } catch (err) {
      console.error('Fehler beim Laden der Daten:', err);
      toast.error('Fehler beim Laden der Kalkulationsdaten');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!claimId || !caseId) return;
    
    setGeneratingPdf(true);
    try {
      const result = await generateCalculationPdf(claimId, caseId);
      setPdfUrl(result.pdfUrl);
      toast.success('PDF erfolgreich erstellt!');
    } catch (err) {
      toast.error(`PDF-Fehler: ${err.message}`);
    } finally {
      setGeneratingPdf(false);
    }
  };

  // No credentials warning
  if (hasCredentials === false) {
    return (
      <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6">
        <div className="flex items-center gap-3 text-amber-800">
          <AlertTriangle className="w-6 h-6" />
          <div>
            <h3 className="font-bold">DAT-Zugang nicht konfiguriert</h3>
            <p className="text-sm">Bitte kontaktieren Sie Ihren Administrator.</p>
          </div>
        </div>
      </div>
    );
  }

  // Success state with calculation data
  if (status === 'success' && claimId) {
    return (
      <div className="space-y-6">
        {/* Success Header */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-green-800">{config.label} erstellt</h2>
                <p className="text-green-600">Claim ID: <strong>{claimId}</strong></p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={fetchContractData}
                disabled={loadingDetails}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {loadingDetails ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Daten abrufen
              </button>
              {contractData?.calculation && (
                <button
                  onClick={handleGeneratePdf}
                  disabled={generatingPdf}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
                >
                  {generatingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  PDF erstellen
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Instructions - show when no calculation data yet */}
        {!contractData?.calculation && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Calculator className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-blue-800 mb-2">Nächste Schritte:</h3>
                <ol className="list-decimal list-inside text-blue-700 space-y-2 text-sm">
                  <li>Wechseln Sie zum Tab <strong>"Glaskalkulation (iFrame)"</strong></li>
                  <li>Führen Sie die Kalkulation im DAT-System durch</li>
                  <li>Speichern Sie die Kalkulation in DAT</li>
                  <li>Kehren Sie hierher zurück und klicken Sie auf <strong>"Daten abrufen"</strong></li>
                </ol>
                <p className="mt-3 text-xs text-blue-600">
                  Die Kalkulationsdaten werden dann automatisch in die Rechnung übernommen.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* PDF Download */}
        {pdfUrl && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-purple-600" />
                <span className="font-medium text-purple-800">Kalkulations-PDF bereit</span>
              </div>
              <a
                href={`${getBackendUrl()}${pdfUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Herunterladen
              </a>
            </div>
          </div>
        )}

        {/* Calculation Summary */}
        {contractData?.calculation && (
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-blue-600" />
                Kalkulationsergebnis
              </h3>
            </div>
            <div className="p-6 space-y-4">
              {/* Totals */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-blue-600 font-medium">Brutto</p>
                  <p className="text-2xl font-bold text-blue-800">
                    {formatCurrency(contractData.calculation.totalGross)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 font-medium">Netto</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {formatCurrency(contractData.calculation.totalNet)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 font-medium">MwSt.</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {formatCurrency(contractData.calculation.totalVat)}
                  </p>
                </div>
              </div>

              {/* Cost Breakdown */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-700 mb-3">Kostenaufschlüsselung</h4>
                <div className="space-y-2">
                  {contractData.calculation.sparePartsTotal && (
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Ersatzteile</span>
                      <span className="font-medium">{formatCurrency(contractData.calculation.sparePartsTotal)}</span>
                    </div>
                  )}
                  {contractData.calculation.labourTotal && (
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Arbeitslohn</span>
                      <span className="font-medium">{formatCurrency(contractData.calculation.labourTotal)}</span>
                    </div>
                  )}
                  {contractData.calculation.lacquerTotal && contractData.calculation.lacquerTotal !== '0.0' && (
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Lackierung</span>
                      <span className="font-medium">{formatCurrency(contractData.calculation.lacquerTotal)}</span>
                    </div>
                  )}
                  {contractData.calculation.auxiliaryTotal && (
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Nebenkosten</span>
                      <span className="font-medium">{formatCurrency(contractData.calculation.auxiliaryTotal)}</span>
                    </div>
                  )}
                  {contractData.calculation.smallParts && (
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Kleinteile ({contractData.calculation.smallPartsPercent || 5}%)</span>
                      <span className="font-medium">{formatCurrency(contractData.calculation.smallParts)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Material Positions */}
              {contractData.calculation.materialPositions?.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-700 mb-3">Ersatzteile ({contractData.calculation.materialPositions.length})</h4>
                  <div className="space-y-2">
                    {contractData.calculation.materialPositions.map((pos, idx) => (
                      <div key={idx} className="flex justify-between py-2 border-b border-gray-100 text-sm">
                        <span className="text-gray-600">{pos.description || pos.partNumber}</span>
                        <span className="font-medium">{formatCurrency(pos.valueTotal)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Labour Positions */}
              {contractData.calculation.labourPositions?.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-700 mb-3">Arbeitspositionen ({contractData.calculation.labourPositions.length})</h4>
                  <div className="space-y-2">
                    {contractData.calculation.labourPositions.map((pos, idx) => (
                      <div key={idx} className="flex justify-between py-2 border-b border-gray-100 text-sm">
                        <span className="text-gray-600">
                          {pos.description} {pos.duration && `(${pos.duration}h)`}
                        </span>
                        <span className="font-medium">{formatCurrency(pos.valueTotal)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 flex-wrap">
          {/* Transfer to Invoice Button - prominently displayed when data exists */}
          {contractData?.calculation && onCalculationComplete && (
            <button
              onClick={() => {
                onCalculationComplete(contractData, claimId, type);
                if (onClose) onClose();
              }}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 font-medium shadow-lg"
            >
              <Euro className="w-5 h-5" />
              Zur Rechnung übernehmen
            </button>
          )}
          <button
            onClick={() => {
              setStatus('idle');
              setClaimId(null);
              setContractData(null);
              setPdfUrl(null);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Neue {config.label} erstellen
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
            >
              Schließen
            </button>
          )}
        </div>
      </div>
    );
  }

  // Form
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Neue {config.label}</h2>
        {caseData && (
          <span className="text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full">
            ✓ Daten aus Fall übernommen
          </span>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Auftragsnummer */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Auftragsnummer *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="z.B. GLAS-2024-001"
            required
            disabled={status === 'loading'}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Fahrzeughalter Section */}
        <div className="border rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b flex items-center gap-2">
            <User className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-gray-700">Fahrzeughalter</h3>
          </div>
          <div className="p-4 grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Anrede</label>
              <select
                name="title"
                value={formData.owner.title}
                onChange={handleOwnerChange}
                disabled={status === 'loading'}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="titleMr">Herr</option>
                <option value="titleMs">Frau</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vorname</label>
              <input
                type="text"
                name="firstName"
                value={formData.owner.firstName}
                onChange={handleOwnerChange}
                placeholder="Max"
                disabled={status === 'loading'}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nachname</label>
              <input
                type="text"
                name="lastName"
                value={formData.owner.lastName}
                onChange={handleOwnerChange}
                placeholder="Mustermann"
                disabled={status === 'loading'}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Straße</label>
              <input
                type="text"
                name="street"
                value={formData.owner.street}
                onChange={handleOwnerChange}
                placeholder="Musterstraße"
                disabled={status === 'loading'}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hausnr.</label>
              <input
                type="text"
                name="streetNumber"
                value={formData.owner.streetNumber}
                onChange={handleOwnerChange}
                placeholder="123"
                disabled={status === 'loading'}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PLZ</label>
              <input
                type="text"
                name="zipCode"
                value={formData.owner.zipCode}
                onChange={handleOwnerChange}
                placeholder="12345"
                disabled={status === 'loading'}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Ort</label>
              <input
                type="text"
                name="city"
                value={formData.owner.city}
                onChange={handleOwnerChange}
                placeholder="Musterstadt"
                disabled={status === 'loading'}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Fahrzeug Section */}
        <div className="border rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b flex items-center gap-2">
            <Car className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-gray-700">Fahrzeug</h3>
          </div>
          <div className="p-4 grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">VIN / Fahrzeugidentnummer *</label>
              <input
                type="text"
                name="vin"
                value={formData.vehicle.vin}
                onChange={handleVehicleChange}
                placeholder="WBADT43452G123456"
                maxLength="17"
                required
                disabled={status === 'loading'}
                className="w-full px-4 py-2 border rounded-lg font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kennzeichen</label>
              <input
                type="text"
                name="licensePlate"
                value={formData.vehicle.licensePlate}
                onChange={handleVehicleChange}
                placeholder="B-AB 1234"
                disabled={status === 'loading'}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Laufleistung (km)</label>
              <input
                type="number"
                name="mileage"
                value={formData.vehicle.mileage}
                onChange={handleVehicleChange}
                placeholder="50000"
                min="0"
                disabled={status === 'loading'}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            <strong>Fehler:</strong> {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={status === 'loading' || !formData.name.trim() || !formData.vehicle.vin.trim()}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Wird erstellt...
              </>
            ) : (
              <>
                <Euro className="w-5 h-5" />
                {config.label} erstellen
              </>
            )}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Abbrechen
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default DatContractForm;
